import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import './darkmode.css';
import './responsive.css';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Surveillance from './pages/Surveillance';
import Assessments from './pages/Assessments';
import Programs from './pages/Programs';
import Vaccinations from './pages/Vaccinations';
import Vitals from './pages/Vitals';
import SDOH from './pages/SDOH';
import HealthEquity from './pages/HealthEquity';
import Reports from './pages/Reports';
import AIInsights from './pages/AIInsights';
import PopulationDataPage from './pages/PopulationData';
import UserManagement from './pages/UserManagement';
import AuditLogs from './pages/AuditLogs';
import Settings from './pages/Settings';
import NotificationsPage from './pages/Notifications';
import WorkspaceManagement from './pages/WorkspaceManagement';
import PublicPage from './pages/PublicPage';
import PricingPage from './pages/PricingPage';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import BlogAdmin from './pages/BlogAdmin';
import SystemHealth from './pages/SystemHealth';
import CookieConsent from './components/CookieConsent';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#020617' }}><div style={{ width: 40, height: 40, border: '4px solid #1e293b', borderTopColor: '#00d4aa', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style></div>;
  if (!user) return <Navigate to="/login" />;
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!['super_admin', 'admin'].includes(user?.role)) return <Navigate to="/dashboard" />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'super_admin') return <Navigate to="/dashboard" />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <CookieConsent />
        <Toaster position="top-right" toastOptions={{ duration: 3000, style: { borderRadius: 10, padding: '12px 20px', fontSize: 14, background: '#0f172a', color: '#f1f5f9', border: '1px solid #1e293b' } }} />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/surveillance" element={<ProtectedRoute><Surveillance /></ProtectedRoute>} />
          <Route path="/assessments" element={<ProtectedRoute><Assessments /></ProtectedRoute>} />
          <Route path="/programs" element={<ProtectedRoute><Programs /></ProtectedRoute>} />
          <Route path="/vaccinations" element={<ProtectedRoute><Vaccinations /></ProtectedRoute>} />
          <Route path="/vitals" element={<ProtectedRoute><Vitals /></ProtectedRoute>} />
          <Route path="/sdoh" element={<ProtectedRoute><SDOH /></ProtectedRoute>} />
          <Route path="/health-equity" element={<ProtectedRoute><HealthEquity /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/ai-insights" element={<ProtectedRoute><AIInsights /></ProtectedRoute>} />
          <Route path="/population" element={<ProtectedRoute><PopulationDataPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminRoute><UserManagement /></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/workspaces" element={<ProtectedRoute><SuperAdminRoute><WorkspaceManagement /></SuperAdminRoute></ProtectedRoute>} />
          <Route path="/admin/system-health" element={<ProtectedRoute><SuperAdminRoute><SystemHealth /></SuperAdminRoute></ProtectedRoute>} />
          <Route path="/audit-logs" element={<ProtectedRoute><AdminRoute><AuditLogs /></AdminRoute></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

          {/* Blog Routes */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin/blog" element={<ProtectedRoute><SuperAdminRoute><BlogAdmin /></SuperAdminRoute></ProtectedRoute>} />

          {/* Public Pages (footer links) */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/:category/:slug" element={<PublicPage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
